import {useCallback} from 'react';

import {SPLITS_EVENTS} from '@/app/main/splits/events/SplitsEvents';
import {PROJECT_TAGS} from '@/constants/ProjectsConstants';
import {useTrackingManager} from '@/hooks';

const cleanMetadata = (metadata?: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(metadata ?? {}).filter(([, value]) => value != null),
  );

export const useSplitsApiTrackers = (
  ownerId?: string | null,
  userId?: string | null,
) => {
  const {trackEvent} = useTrackingManager();

  const trackSplitsApiEvent = useCallback(
    (event: string, metadata?: Record<string, unknown>) => {
      trackEvent(event, {
        project_tag: PROJECT_TAGS.SPLITS,
        ...(ownerId && {user: {id: ownerId}}),
        ...(userId && {user: {id: userId}}),
        metadata: cleanMetadata(metadata),
      });
    },
    [ownerId, trackEvent, userId],
  );

  const trackApiSplitsSaveParticipantSuccess = useCallback(
    (
      splitId?: string | null,
      phone?: string | null,
      participantId?: string | null,
    ) => {
      trackSplitsApiEvent(SPLITS_EVENTS.API_SPLITS_SAVE_PARTICIPANT_SUCCESS, {
        split_id: splitId,
        phone,
        participant_id: participantId,
      });
    },
    [trackSplitsApiEvent],
  );

  const trackApiSplitsSaveParticipantError = useCallback(
    (splitId?: string | null, phone?: string | null, error?: string) => {
      trackSplitsApiEvent(SPLITS_EVENTS.API_SPLITS_SAVE_PARTICIPANT_ERROR, {
        split_id: splitId,
        phone,
        error,
      });
    },
    [trackSplitsApiEvent],
  );

  const trackApiSplitsConfirmPaymentSuccess = useCallback(
    (splitId?: string | null, participantId?: string | null) => {
      trackSplitsApiEvent(SPLITS_EVENTS.API_SPLITS_CONFIRM_PAYMENT_SUCCESS, {
        split_id: splitId,
        participant_id: participantId,
      });
    },
    [trackSplitsApiEvent],
  );

  const trackApiSplitsConfirmPaymentError = useCallback(
    (
      splitId?: string | null,
      participantId?: string | null,
      error?: string,
    ) => {
      trackSplitsApiEvent(SPLITS_EVENTS.API_SPLITS_CONFIRM_PAYMENT_ERROR, {
        split_id: splitId,
        participant_id: participantId,
        error,
      });
    },
    [trackSplitsApiEvent],
  );

  const trackApiSplitsValidateOtpSuccess = useCallback(
    (splitId?: string | null, participantId?: string | null) => {
      trackSplitsApiEvent(SPLITS_EVENTS.API_SPLITS_VALIDATE_OTP_SUCCESS, {
        split_id: splitId,
        participant_id: participantId,
      });
    },
    [trackSplitsApiEvent],
  );

  const trackApiSplitsValidateOtpError = useCallback(
    (
      splitId?: string | null,
      participantId?: string | null,
      error?: string,
    ) => {
      trackSplitsApiEvent(SPLITS_EVENTS.API_SPLITS_VALIDATE_OTP_ERROR, {
        split_id: splitId,
        participant_id: participantId,
        error,
      });
    },
    [trackSplitsApiEvent],
  );

  const trackApiSplitsResendOtpSuccess = useCallback(
    (splitId?: string | null, participantId?: string | null) => {
      trackSplitsApiEvent(SPLITS_EVENTS.API_SPLITS_RESEND_OTP_SUCCESS, {
        split_id: splitId,
        participant_id: participantId,
      });
    },
    [trackSplitsApiEvent],
  );

  const trackApiSplitsResendOtpError = useCallback(
    (
      splitId?: string | null,
      participantId?: string | null,
      error?: string,
    ) => {
      trackSplitsApiEvent(SPLITS_EVENTS.API_SPLITS_RESEND_OTP_ERROR, {
        split_id: splitId,
        participant_id: participantId,
        error,
      });
    },
    [trackSplitsApiEvent],
  );

  const trackApiSplitsJoinLoggedUserToSplitSuccess = useCallback(
    (splitId?: string | null, participantId?: string | null) => {
      trackSplitsApiEvent(
        SPLITS_EVENTS.API_SPLITS_JOIN_LOGGED_USER_TO_SPLIT_SUCCESS,
        {
          split_id: splitId,
          participant_id: participantId,
        },
      );
    },
    [trackSplitsApiEvent],
  );

  const trackApiSplitsJoinLoggedUserToSplitError = useCallback(
    (
      splitId?: string | null,
      participantId?: string | null,
      error?: string,
    ) => {
      trackSplitsApiEvent(
        SPLITS_EVENTS.API_SPLITS_JOIN_LOGGED_USER_TO_SPLIT_ERROR,
        {
          split_id: splitId,
          participant_id: participantId,
          error,
        },
      );
    },
    [trackSplitsApiEvent],
  );

  return {
    trackApiSplitsSaveParticipantSuccess,
    trackApiSplitsSaveParticipantError,
    trackApiSplitsConfirmPaymentSuccess,
    trackApiSplitsConfirmPaymentError,
    trackApiSplitsValidateOtpSuccess,
    trackApiSplitsValidateOtpError,
    trackApiSplitsResendOtpSuccess,
    trackApiSplitsResendOtpError,
    trackApiSplitsJoinLoggedUserToSplitSuccess,
    trackApiSplitsJoinLoggedUserToSplitError,
  };
};
